import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, CardActions, Button, Chip,
  TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem,
  Grid, Divider, Link, Tooltip, CircularProgress, Alert
} from '@mui/material';
import {
  Search, PictureAsPdf, Description, TableChart, Image,
  Link as LinkIcon, InsertDriveFile, Download, OpenInNew,
  PushPin, CalendarMonth, Article, Folder, School, SportsSoccer
} from '@mui/icons-material';
import { CATEGORIES } from '../config/resourcesConfig';
import apiClient from '../services/apiClient';

const FILE_ICONS = {
  pdf:   { icon: PictureAsPdf, color: '#e53935' },
  docx:  { icon: Description,  color: '#1565c0' },
  xlsx:  { icon: TableChart,   color: '#2e7d32' },
  image: { icon: Image,        color: '#6a1b9a' },
  link:  { icon: LinkIcon,     color: '#00838f' },
  other: { icon: InsertDriveFile, color: '#546e7a' },
};

const CATEGORY_ICONS = {
  'School Calendar':      CalendarMonth,
  'Newsletter':           Article,
  'Forms & Applications': Folder,
  'Handbooks & Policies': Description,
  'Curriculum':           School,
  'Athletics':            SportsSoccer,
  'Other':                InsertDriveFile,
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const ResourceCard = ({ resource }) => {
  const fileInfo = FILE_ICONS[resource.file_type] || FILE_ICONS.other;
  const FileIcon = fileInfo.icon;
  const isLink = resource.file_type === 'link';

  return (
    <Card
      elevation={resource.pinned ? 2 : 1}
      sx={{
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: resource.pinned ? '1px solid' : '1px solid transparent',
        borderColor: resource.pinned ? 'primary.light' : 'transparent',
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: 4 },
      }}
    >
      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 1, flexShrink: 0,
            bgcolor: `${fileInfo.color}15`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <FileIcon sx={{ color: fileInfo.color, fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              {resource.pinned && (
                <Tooltip title="Pinned">
                  <PushPin sx={{ fontSize: 14, color: 'primary.main', transform: 'rotate(45deg)' }} />
                </Tooltip>
              )}
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                {resource.name}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {formatDate(resource.uploaded_at)}{resource.file_size ? ` · ${resource.file_size}` : ''}
            </Typography>
          </Box>
        </Box>

        {resource.description && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.5 }}>
            {resource.description}
          </Typography>
        )}
      </CardContent>

      <CardActions sx={{ px: 2, pb: 1.5, pt: 0 }}>
        <Button
          size="small"
          variant="outlined"
          href={resource.s3_url}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={isLink ? <OpenInNew /> : <Download />}
          component={Link}
          sx={{ borderRadius: 1.5, textTransform: 'none', fontSize: '0.8rem' }}
        >
          {isLink ? 'Open Link' : 'Download'}
        </Button>
      </CardActions>
    </Card>
  );
};

/**
 * Shared Resources tab for Student, Teacher, and Parent portals.
 * @param {string} role — 'student' | 'teacher' | 'parent'
 */
const ResourcesTab = ({ role = 'all' }) => {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [allResources, setAllResources] = useState([]);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    apiClient.getDocuments(role === 'all' ? null : role)
      .then(data => setAllResources(data.documents || []))
      .catch(err => setLoadError(err.message));
  }, [role]);

  const usedCategories = useMemo(
    () => CATEGORIES.filter(c => allResources.some(r => r.category === c)),
    [allResources]
  );

  const filtered = useMemo(() => {
    return allResources.filter(r => {
      const matchSearch = !search ||
        r.name.toLowerCase().includes(search.toLowerCase()) ||
        (r.description || '').toLowerCase().includes(search.toLowerCase());
      const matchCat = categoryFilter === 'all' || r.category === categoryFilter;
      return matchSearch && matchCat;
    });
  }, [allResources, search, categoryFilter]);

  const grouped = useMemo(() => {
    const cats = categoryFilter === 'all' ? usedCategories : [categoryFilter];
    return cats
      .map(cat => ({
        category: cat,
        items: filtered
          .filter(r => r.category === cat)
          .sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0)),
      }))
      .filter(g => g.items.length > 0);
  }, [filtered, categoryFilter, usedCategories]);

  if (loadError) return <Alert severity="error">Failed to load resources: {loadError}</Alert>;

  return (
    <Box>
      {/* Search + filter bar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search resources…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 260, flexGrow: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Category</InputLabel>
          <Select value={categoryFilter} label="Category" onChange={e => setCategoryFilter(e.target.value)}>
            <MenuItem value="all">All Categories</MenuItem>
            {usedCategories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {allResources.length === 0 && grouped.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
          <CircularProgress size={32} sx={{ mb: 2, opacity: 0.4 }} />
          <Typography variant="body1">Loading resources…</Typography>
        </Box>
      ) : grouped.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
          <InsertDriveFile sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
          <Typography variant="body1">No resources found.</Typography>
        </Box>
      ) : grouped.length > 0 ? (
        grouped.map(({ category, items }) => {
          const CatIcon = CATEGORY_ICONS[category] || InsertDriveFile;
          return (
            <Box key={category} sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CatIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{category}</Typography>
                <Chip label={items.length} size="small" sx={{ ml: 0.5 }} />
              </Box>
              <Grid container spacing={2}>
                {items.map(r => (
                  <Grid item xs={12} sm={6} md={4} key={r.id}>
                    <ResourceCard resource={r} />
                  </Grid>
                ))}
              </Grid>
              <Divider sx={{ mt: 3 }} />
            </Box>
          );
        })
      ) : null}
    </Box>
  );
};

export default ResourcesTab;
